import type { BillingErrorsCopy } from './appCopy';
import { getAppCopyForLocale } from './appCopy';
import { detectLocale, type AppLocale } from './locale';
import { APP_STORE_PRODUCT_NOT_LOADED } from '../services/entitlement/types';

/** StoreKit / 課金プラグインが返すキャンセル文言（英語・日本語・中国語） */
const PURCHASE_CANCEL_PATTERNS = [
  /^the user cancelled the order\.?$/i,
  /^user cancelled( the order)?\.?$/i,
  /^cancelled$/i,
  /^purchase (was )?cancelled\.?$/i,
  /^payment (was )?cancelled\.?$/i,
  /Error invoking remote method 'pouches-store-purchase': Error: cancelled$/i,
  /Error invoking remote method "pouches-store-purchase": Error: cancelled$/i,
  /ユーザーが.*キャンセル/,
  /注文をキャンセル/,
  /購入をキャンセル/,
  /用户取消了订单/,
  /购买已取消/,
  /已取消购买/,
];

/** billing API が返す英語メッセージ → 各ロケール表示 */
const REDEEM_API_ERROR_LABELS: Record<string, Record<AppLocale, string>> = {
  'Redeem code not found or already used': {
    en: 'Redeem code not found or already used',
    ja: '引き換えコードが見つからないか、すでに使用されています',
    zh: '未找到兑换码或已被使用',
  },
  'Redeem code has expired': {
    en: 'Redeem code has expired',
    ja: '引き換えコードの有効期限が切れています',
    zh: '兑换码已过期',
  },
  'This browser is already registered': {
    en: 'This browser is already registered',
    ja: 'このブラウザはすでに登録済みです',
    zh: '此浏览器已注册',
  },
  'Device limit reached for this subscription': {
    en: 'Device limit reached for this subscription',
    ja: 'このサブスクリプションの登録端末数の上限に達しています',
    zh: '此订阅的设备注册数已达上限',
  },
  'Invalid redeem code record': {
    en: 'Could not process this redeem code.',
    ja: '引き換えコードの処理に失敗しました',
    zh: '兑换码处理失败',
  },
  'Please wait before issuing another code': {
    en: 'Please wait before issuing another code',
    ja: 'しばらく待ってから再度コードを発行してください',
    zh: '请稍后再发放兑换码',
  },
  'No active Microsoft subscription found': {
    en: 'No active Microsoft subscription found',
    ja: '有効な Microsoft Store サブスクリプションが見つかりません',
    zh: '未找到有效的 Microsoft Store 订阅',
  },
  'Microsoft subscription verification failed': {
    en: 'Microsoft subscription verification failed',
    ja: 'Microsoft Store サブスクリプションの確認に失敗しました',
    zh: 'Microsoft Store 订阅验证失败',
  },
  'Microsoft OAuth token request failed': {
    en: 'Could not connect to Microsoft Store',
    ja: 'Microsoft Store に接続できませんでした',
    zh: '无法连接到 Microsoft Store',
  },
  'Microsoft OAuth token missing': {
    en: 'Could not connect to Microsoft Store',
    ja: 'Microsoft Store に接続できませんでした',
    zh: '无法连接到 Microsoft Store',
  },
  'Microsoft API credentials are not configured': {
    en: 'Could not issue a Web redeem code.',
    ja: 'Web版用コードを発行できませんでした。',
    zh: '无法发放 Web 版兑换码。',
  },
  'Apple subscription verification failed': {
    en: 'Apple subscription verification failed',
    ja: 'App Store サブスクリプションの確認に失敗しました',
    zh: 'App Store 订阅验证失败',
  },
  'No active Apple subscription found': {
    en: 'No active Apple subscription found',
    ja: '有効な App Store サブスクリプションが見つかりません',
    zh: '未找到有效的 App Store 订阅',
  },
  'Apple subscription request failed': {
    en: 'Apple subscription request failed',
    ja: 'App Store への確認リクエストに失敗しました',
    zh: 'App Store 订阅确认请求失败',
  },
  'Apple API credentials are not configured': {
    en: 'Could not issue a Web redeem code.',
    ja: 'Web版用コードを発行できませんでした。',
    zh: '无法发放 Web 版兑换码。',
  },
  'Invalid Apple private key': {
    en: 'Could not issue a Web redeem code.',
    ja: 'Web版用コードを発行できませんでした。',
    zh: '无法发放 Web 版兑换码。',
  },
  'Failed to sign Apple JWT': {
    en: 'Could not issue a Web redeem code.',
    ja: 'Web版用コードを発行できませんでした。',
    zh: '无法发放 Web 版兑换码。',
  },
  'Invalid signed transaction': {
    en: 'Apple subscription verification failed',
    ja: 'App Store サブスクリプションの確認に失敗しました',
    zh: 'App Store 订阅验证失败',
  },
  'Invalid signed transaction payload': {
    en: 'Apple subscription verification failed',
    ja: 'App Store サブスクリプションの確認に失敗しました',
    zh: 'App Store 订阅验证失败',
  },
  'Unsupported source': {
    en: 'Web redeem codes cannot be issued from this device.',
    ja: 'この端末からは Web 版用コードを発行できません。',
    zh: '无法从此设备发放 Web 版兑换码。',
  },
  'store_subscription_id is required': {
    en: 'An active subscription is required to issue a code.',
    ja: 'コードを発行するには有効なサブスクリプションが必要です。',
    zh: '需要有效订阅才能发放兑换码。',
  },
  'original_transaction_id is required': {
    en: 'An active App Store subscription is required to issue a code.',
    ja: 'コードを発行するには App Store の有効なサブスクが必要です。',
    zh: '需要有效的 App Store 订阅才能发放兑换码。',
  },
  Unauthorized: {
    en: 'Could not issue a Web redeem code.',
    ja: 'Web版用コードを発行できませんでした。',
    zh: '无法发放 Web 版兑换码。',
  },
  'Failed to generate unique redeem code': {
    en: 'Could not issue a Web redeem code.',
    ja: 'Web版用コードを発行できませんでした。',
    zh: '无法发放 Web 版兑换码。',
  },
  'Method not allowed': {
    en: 'Billing API error',
    ja: '課金 API エラー',
    zh: '计费 API 错误',
  },
  'device_id is required': {
    en: 'Billing API error',
    ja: '課金 API エラー',
    zh: '计费 API 错误',
  },
};

/** 英語正本（小文字キー）→ 各ロケール表示 */
const PLUGIN_ERROR_LABELS: Record<string, Record<AppLocale, string>> = {
  'product not found': {
    en: 'Product not found',
    ja: '商品が見つかりません',
    zh: '未找到商品',
  },
  'cannot connect to itunes store': {
    en: 'Cannot connect to the App Store',
    ja: 'App Store に接続できません',
    zh: '无法连接到 App Store',
  },
  'unable to purchase': {
    en: 'Unable to complete purchase',
    ja: '購入を完了できませんでした',
    zh: '无法完成购买',
  },
  'item already owned': {
    en: 'You already own this subscription',
    ja: 'このサブスクリプションは既に購入済みです',
    zh: '您已拥有此订阅',
  },
};

export function isPurchaseCancelledMessage(message: string): boolean {
  const trimmed = message.trim();
  if (!trimmed) return false;
  return PURCHASE_CANCEL_PATTERNS.some((pattern) => pattern.test(trimmed));
}

/** Electron IPC が付ける pouches-store-purchase のラッパーを外す */
export function unwrapStorePurchaseIpcError(message: string): string {
  const trimmed = message.trim();
  const match =
    /^Error invoking remote method ['"]pouches-store-purchase['"]: Error: (.+)$/i.exec(trimmed);
  return match ? match[1].trim() : trimmed;
}

const APP_STORE_PRODUCT_NOT_LOADED_LABELS: Record<AppLocale, string> = {
  en: 'Could not load the subscription from the App Store. Check your network connection and Sandbox account, then try again.',
  ja: 'App Store からサブスクリプション情報を取得できませんでした。ネットワークと Sandbox アカウントを確認してから再度お試しください。',
  zh: '无法从 App Store 加载订阅信息。请检查网络连接和 Sandbox 账户后重试。',
};

function mapInternalBillingError(
  message: string,
  be: BillingErrorsCopy,
  locale: AppLocale,
): string | null {
  if (message === APP_STORE_PRODUCT_NOT_LOADED) {
    return APP_STORE_PRODUCT_NOT_LOADED_LABELS[locale] ?? APP_STORE_PRODUCT_NOT_LOADED_LABELS.en;
  }
  if (message === 'Purchases are not available on this platform yet.') {
    return be.purchaseUnavailable;
  }
  if (message === 'Store purchases are not available when running as administrator.') {
    return be.purchaseUnavailable;
  }
  if (message === 'MS_STORE_PURCHASE_NO_INVENTORY') {
    return be.storePurchaseNoInventory;
  }
  if (message === 'MS_STORE_PURCHASE_NOT_COMPLETED') {
    return be.storePurchaseNotCompleted;
  }
  if (message === 'MS_STORE_PURCHASE_FAILED') {
    return be.storePurchaseFailed;
  }
  if (message === 'MS_STORE_LICENSE_NOT_REFLECTED') {
    return be.storeLicenseNotReflected;
  }
  if (message === 'Subscription management is not available on this platform yet.') {
    return be.manageUnavailable;
  }
  if (message === 'Checkout URL was not returned.') {
    return be.checkoutUrlMissing;
  }
  const billingApiMatch = /^Billing API error \((\d+)\)$/.exec(message);
  if (billingApiMatch) {
    return be.billingApiError(Number(billingApiMatch[1]));
  }
  const statusCheckMatch = /^Status check failed \((\d+)\)$/.exec(message);
  if (statusCheckMatch) {
    return be.statusCheckFailed(Number(statusCheckMatch[1]));
  }
  return null;
}

function localizePluginError(message: string, locale: AppLocale): string | null {
  const labels = PLUGIN_ERROR_LABELS[message.trim().toLowerCase()];
  if (!labels) return null;
  return labels[locale] ?? labels.en;
}

function isBillingNetworkErrorMessage(message: string): boolean {
  const lower = message.trim().toLowerCase();
  return (
    lower === 'failed to fetch' ||
    lower === 'load failed' ||
    lower.includes('networkerror when attempting to fetch resource')
  );
}

function localizeRedeemApiError(
  message: string,
  locale: AppLocale,
  be: BillingErrorsCopy
): string | null {
  const trimmed = message.trim();
  if (trimmed === 'Invalid redeem code') {
    return be.redeemInvalidCode;
  }
  const labels = REDEEM_API_ERROR_LABELS[trimmed];
  if (!labels) return null;
  return labels[locale] ?? labels.en;
}

/**
 * 課金エラーメッセージを表示用にローカライズ。
 * 購入キャンセル時は null（エラー非表示）。
 */
export function localizeEntitlementError(
  message: string,
  locale?: AppLocale
): string | null {
  const trimmed = message.trim();
  if (!trimmed) return null;
  if (isPurchaseCancelledMessage(trimmed)) return null;

  const loc = locale ?? detectLocale();
  const core = unwrapStorePurchaseIpcError(trimmed);
  const be = getAppCopyForLocale(loc).billingErrors;
  const internal = mapInternalBillingError(core, be, loc);
  if (internal) return internal;

  const redeemApi = localizeRedeemApiError(core, loc, be);
  if (redeemApi) return redeemApi;

  if (isBillingNetworkErrorMessage(core)) {
    return be.billingNetworkError;
  }

  const plugin = localizePluginError(trimmed, loc);
  if (plugin) return plugin;

  return trimmed;
}
